import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  BroadcastDto,
  BroadcastLandingDto,
  BroadcastSendReportDto,
  CommishOverviewDto,
  CreateInviteBody,
  EligibleGamesQuery,
  HomeDto,
  InviteDto,
  LeagueMemberDto,
  OddsRefreshReportDto,
  Paginated,
  ProviderQuotaDto,
  ResetLinkDto,
  RosterDto,
  RosterSyncReportDto,
  ScheduleGameDto,
  SessionDto,
  SettleWeekReportDto,
  SlateEventDto,
  SlateGenerationReportDto,
  StandingsDto,
  SyncReportDto,
  UpdateSeasonConfigBody,
  WeekDetailDto,
  WeekListItemDto,
  WeekResultsDto,
} from '@pickem/shared';
import { api, ApiError } from './client';

/**
 * Query keys.
 *
 * Everything a commissioner action can change hangs off a week, so a slate mutation
 * invalidates exactly one subtree rather than blowing the whole cache away.
 */
export const keys = {
  session: ['session'] as const,
  home: ['home'] as const,
  week: (weekId: string) => ['week', weekId] as const,
  weekResults: (weekId: string) => ['week', weekId, 'results'] as const,
  weekAudit: (weekId: string) => ['week', weekId, 'audit'] as const,
  weekList: (seasonId: string) => ['season', seasonId, 'weeks'] as const,
  standings: (seasonId: string) => ['season', seasonId, 'standings'] as const,
  members: (leagueId: string) => ['league', leagueId, 'members'] as const,
  invites: (leagueId: string) => ['league', leagueId, 'invites'] as const,
  commishOverview: ['commish', 'overview'] as const,
  broadcasts: (leagueId: string) => ['league', leagueId, 'broadcasts'] as const,
  broadcastLanding: (slug: string) => ['broadcast', slug] as const,
  providerQuota: ['providers', 'quota'] as const,
  roster: (year: number) => ['roster', year] as const,
  eligible: (weekId: string, q: Partial<EligibleGamesQuery>) =>
    ['week', weekId, 'eligible', q] as const,
};

/** A 401 is a real answer, not a failure worth retrying. */
function retryPolicy(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && !error.isRetryable) return false;
  return failureCount < 2;
}

const defaults = { retry: retryPolicy, staleTime: 15_000 } satisfies Partial<UseQueryOptions>;

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<SessionDto>('/auth/me'),
    // An anonymous visitor is a normal state; don't retry into a 401 loop.
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<SessionDto>('/auth/login', body),
    onSuccess: (session) => {
      qc.setQueryData(keys.session, session);
      void qc.invalidateQueries();
    },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      displayName: string;
      inviteCode: string;
    }) => api.post<SessionDto>('/auth/register', body),
    onSuccess: (session) => {
      qc.setQueryData(keys.session, session);
      void qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (body: { email: string }) => api.post<void>('/auth/forgot-password', body),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api.post<void>('/auth/reset-password', body),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { displayName?: string; currentPassword?: string; newPassword?: string }) =>
      api.patch<SessionDto>('/me', body),
    onSuccess: (session) => qc.setQueryData(keys.session, session),
  });
}

/**
 * Profile picture upload.
 *
 * The session is refetched rather than patched, because the server owns the version number
 * in the URL — guessing it client-side would be the one way to end up with a cached
 * picture that never updates.
 */
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mimeType: string; data: string }) =>
      api.put<{ avatarUrl: string }>('/me/avatar', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.session });
      // The picture appears on the leaderboard too, so those lists are now stale.
      void qc.invalidateQueries({ queryKey: ['season'] });
      void qc.invalidateQueries({ queryKey: ['league'] });
    },
  });
}

export function useRemoveAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/me/avatar'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.session });
      void qc.invalidateQueries({ queryKey: ['season'] });
      void qc.invalidateQueries({ queryKey: ['league'] });
    },
  });
}

export function useJoinLeague() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { inviteCode: string }) => api.post<SessionDto>('/leagues/join', body),
    onSuccess: (session) => {
      qc.setQueryData(keys.session, session);
      void qc.invalidateQueries();
    },
  });
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useHome(enabled = true) {
  return useQuery({
    ...defaults,
    queryKey: keys.home,
    queryFn: () => api.get<HomeDto>('/me/home'),
    enabled,
    // The live week's lock states drift in real time, so the dashboard refreshes itself
    // while it is on screen rather than showing a game as open after kickoff.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useWeek(weekId: string | null | undefined) {
  return useQuery({
    ...defaults,
    queryKey: keys.week(weekId ?? ''),
    queryFn: () => api.get<WeekDetailDto>(`/weeks/${weekId}`),
    enabled: Boolean(weekId),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useWeekResults(weekId: string | null | undefined) {
  return useQuery({
    ...defaults,
    queryKey: keys.weekResults(weekId ?? ''),
    queryFn: () => api.get<WeekResultsDto>(`/weeks/${weekId}/results`),
    enabled: Boolean(weekId),
  });
}

export function useWeekList(seasonId: string | null | undefined) {
  return useQuery({
    ...defaults,
    queryKey: keys.weekList(seasonId ?? ''),
    queryFn: () => api.get<WeekListItemDto[]>(`/seasons/${seasonId}/weeks`),
    enabled: Boolean(seasonId),
  });
}

export function useStandings(seasonId: string | null | undefined) {
  return useQuery({
    ...defaults,
    queryKey: keys.standings(seasonId ?? ''),
    queryFn: () => api.get<StandingsDto>(`/seasons/${seasonId}/standings`),
    enabled: Boolean(seasonId),
  });
}

export function useMembers(leagueId: string | null | undefined) {
  return useQuery({
    ...defaults,
    queryKey: keys.members(leagueId ?? ''),
    queryFn: () => api.get<LeagueMemberDto[]>(`/leagues/${leagueId}/members`),
    enabled: Boolean(leagueId),
  });
}

// ---------------------------------------------------------------------------
// Commissioner
// ---------------------------------------------------------------------------

export function useCommishOverview(enabled = true) {
  return useQuery({
    ...defaults,
    queryKey: keys.commishOverview,
    queryFn: () => api.get<CommishOverviewDto>('/commish/overview'),
    enabled,
  });
}

export function useEligibleGames(weekId: string | null, query: Partial<EligibleGamesQuery>) {
  return useQuery({
    ...defaults,
    queryKey: keys.eligible(weekId ?? '', query),
    queryFn: () =>
      api.get<Paginated<ScheduleGameDto>>(`/weeks/${weekId}/eligible-games`, {
        level: query.level,
        q: query.q,
        conference: query.conference,
        date: query.date,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
      }),
    enabled: Boolean(weekId),
    // Keeps the previous page on screen while the next one loads, so the browser does not
    // flash empty every time the commissioner types a letter.
    placeholderData: (prev) => prev,
  });
}

export function useWeekAudit(weekId: string | null, enabled = true) {
  return useQuery({
    ...defaults,
    queryKey: keys.weekAudit(weekId ?? ''),
    queryFn: () => api.get<SlateEventDto[]>(`/weeks/${weekId}/audit`),
    enabled: Boolean(weekId) && enabled,
  });
}

export function useInvites(leagueId: string | null | undefined, enabled = true) {
  return useQuery({
    ...defaults,
    queryKey: keys.invites(leagueId ?? ''),
    queryFn: () => api.get<InviteDto[]>(`/leagues/${leagueId}/invites`),
    enabled: Boolean(leagueId) && enabled,
  });
}

/**
 * Every slate mutation refreshes the same set of things, because a change to the slate can
 * move the dashboard, the standings and the commissioner's counters at once.
 */
function useSlateInvalidator(weekId: string | null) {
  const qc = useQueryClient();
  return () => {
    if (weekId) {
      void qc.invalidateQueries({ queryKey: ['week', weekId] });
    }
    void qc.invalidateQueries({ queryKey: keys.home });
    void qc.invalidateQueries({ queryKey: keys.commishOverview });
    void qc.invalidateQueries({ queryKey: ['season'] });
  };
}

export function useGenerateSlate(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: () => api.post<SlateGenerationReportDto>(`/weeks/${weekId}/slate/generate`, {}),
    onSuccess: invalidate,
  });
}

export function useRerollAll(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (excludeCurrent: boolean) =>
      api.post<SlateGenerationReportDto>(`/weeks/${weekId}/slate/reroll`, { excludeCurrent }),
    onSuccess: invalidate,
  });
}

export function useRerollOne(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (slateGameId: string) =>
      api.post<WeekDetailDto>(`/slate-games/${slateGameId}/reroll`),
    onSuccess: invalidate,
  });
}

export function useReplaceSlateGame(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (args: {
      slateGameId: string;
      scheduleGameId?: string;
      contestSpreadHome?: number;
      contestKickoffAt?: string;
      acknowledgePicksAffected?: boolean;
      reason?: string;
    }) => {
      const { slateGameId, ...body } = args;
      return api.put<WeekDetailDto>(`/slate-games/${slateGameId}`, body);
    },
    onSuccess: invalidate,
  });
}

export function useAddSlateGame(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (args: { scheduleGameId: string; contestSpreadHome?: number }) =>
      api.post<WeekDetailDto>(`/weeks/${weekId}/slate/games`, args),
    onSuccess: invalidate,
  });
}

export function useRemoveSlateGame(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (args: {
      slateGameId: string;
      reason?: string;
      acknowledgePicksAffected?: boolean;
    }) =>
      api.delete<WeekDetailDto>(`/slate-games/${args.slateGameId}`, {
        reason: args.reason,
        acknowledgePicksAffected: args.acknowledgePicksAffected,
      }),
    onSuccess: invalidate,
  });
}

export function usePublishWeek(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (allowPastKickoffs: boolean) =>
      api.post<{ warnings: string[]; week: WeekDetailDto }>(`/weeks/${weekId}/publish`, {
        allowPastKickoffs,
      }),
    onSuccess: invalidate,
  });
}

export function useUnpublishWeek(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (force: boolean) =>
      api.post<WeekDetailDto>(`/weeks/${weekId}/unpublish`, undefined, { force }),
    onSuccess: invalidate,
  });
}

export function useSetWeekLock(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (mode: 'lock' | 'unlock' | 'force-unlock') =>
      api.post<WeekDetailDto>(`/weeks/${weekId}/${mode}`),
    onSuccess: invalidate,
  });
}

export function useSetResult(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (args: {
      slateGameId: string;
      homeScore: number;
      awayScore: number;
      status?: 'final' | 'in_progress' | 'scheduled' | 'postponed' | 'canceled';
    }) => {
      const { slateGameId, ...body } = args;
      return api.put<{ result: string; picksUpdated: number; week: WeekDetailDto }>(
        `/slate-games/${slateGameId}/result`,
        { ...body, status: body.status ?? 'final' },
      );
    },
    onSuccess: invalidate,
  });
}

export function useOverrideGrade(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: (args: { slateGameId: string; result: string; reason: string }) => {
      const { slateGameId, ...body } = args;
      return api.put<{ picksUpdated: number; week: WeekDetailDto }>(
        `/slate-games/${slateGameId}/grade`,
        body,
      );
    },
    onSuccess: invalidate,
  });
}

/**
 * Pull the latest scores and settle every finished game in a week.
 *
 * Free: it reads the scoreboard feed, which is unmetered. Kept separate from
 * `useRefreshOdds` for the same reason the server keeps the routes separate — nothing that
 * can spend a credit should share a button with something that cannot.
 */
export function useSettleWeek(weekId: string | null) {
  const invalidate = useSlateInvalidator(weekId);
  return useMutation({
    mutationFn: () =>
      api.post<SettleWeekReportDto & { week: WeekDetailDto }>(`/weeks/${weekId}/regrade`, {}),
    onSuccess: invalidate,
  });
}

export function useUpdateSeasonConfig(seasonId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSeasonConfigBody) => api.put(`/seasons/${seasonId}/config`, body),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

export function useCreateInvite(leagueId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateInviteBody>) =>
      api.post<{ id: string; code: string }>(`/leagues/${leagueId}/invites`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invites(leagueId ?? '') }),
  });
}

export function useRevokeInvite(leagueId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.delete<void>(`/invites/${inviteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invites(leagueId ?? '') }),
  });
}

/**
 * Mint a one-time reset link for a member who has forgotten their password.
 *
 * Nothing is cached: the link is a bearer token, and holding it in a query cache would
 * leave it recoverable long after the commissioner dismissed it from the screen.
 */
export function useResetMemberPassword() {
  return useMutation({
    mutationFn: (membershipId: string) =>
      api.post<ResetLinkDto>(`/league-members/${membershipId}/password-reset`),
  });
}

export function useUpdateMember(leagueId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      membershipId: string;
      role?: string;
      status?: string;
      rosterAccess?: boolean;
    }) => {
      const { membershipId, ...body } = args;
      return api.patch<LeagueMemberDto[]>(`/league-members/${membershipId}`, body);
    },
    onSuccess: (data) => {
      if (leagueId) qc.setQueryData(keys.members(leagueId), data);
      void qc.invalidateQueries({ queryKey: keys.home });
    },
  });
}

// ---------------------------------------------------------------------------
// Upstream feeds
// ---------------------------------------------------------------------------

/**
 * Remaining metered budget. Reading it is free — the server reports the last balance the
 * upstream told us rather than calling out to ask, so this can poll like any other query.
 */
export function useProviderQuota(enabled = true) {
  return useQuery({
    ...defaults,
    queryKey: keys.providerQuota,
    queryFn: () => api.get<ProviderQuotaDto>('/admin/providers/quota'),
    enabled,
  });
}

/** Teams, schedule, kickoffs and scores. Free, so this is safe to press repeatedly. */
export function useSyncSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { level: 'nfl' | 'cfb'; seasonYear: number; week?: number }) =>
      api.post<SyncReportDto>('/admin/sync/schedule', args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] });
    },
  });
}

/**
 * Pull spreads. The only mutation in the client that can spend a credit, which is why it
 * is its own hook rather than a flag on the schedule sync — nothing should be able to
 * reach it by accident.
 */
export function useRefreshOdds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { seasonYear: number; level?: 'nfl' | 'cfb'; force?: boolean }) =>
      api.post<OddsRefreshReportDto>('/admin/providers/odds', args),
    onSuccess: (report) => {
      // The response already carries the new balance, so seed it rather than refetching.
      qc.setQueryData(keys.providerQuota, report.quota);
      void qc.invalidateQueries({ queryKey: ['week'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Broadcasts
// ---------------------------------------------------------------------------

/**
 * The hand-written notification.
 *
 * Every mutation here invalidates the same list, which is the whole cache surface — a
 * broadcast has no relationship to a week, a pick or a standing, so nothing else can go
 * stale because of one.
 */
function useBroadcastInvalidator(leagueId: string | null) {
  const qc = useQueryClient();
  return () => {
    if (leagueId) void qc.invalidateQueries({ queryKey: keys.broadcasts(leagueId) });
  };
}

export function useBroadcasts(leagueId: string | null) {
  return useQuery({
    ...defaults,
    queryKey: keys.broadcasts(leagueId ?? ''),
    queryFn: () => api.get<BroadcastDto[]>(`/leagues/${leagueId}/broadcasts`),
    enabled: Boolean(leagueId),
  });
}

export interface BroadcastDraftValues {
  title: string;
  body: string;
  landingKicker: string | null;
  landingHeadline: string | null;
  landingBody: string | null;
  targetPath: string | null;
}

export function useCreateBroadcast(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (values: BroadcastDraftValues) =>
      api.post<BroadcastDto>(`/leagues/${leagueId}/broadcasts`, values),
    onSuccess: invalidate,
  });
}

export function useUpdateBroadcast(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (args: { id: string; values: BroadcastDraftValues }) =>
      api.put<BroadcastDto>(`/broadcasts/${args.id}`, args.values),
    onSuccess: invalidate,
  });
}

export function useBroadcastImage(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (args: { id: string; mimeType: string; data: string }) =>
      api.put<BroadcastDto>(`/broadcasts/${args.id}/image`, {
        mimeType: args.mimeType,
        data: args.data,
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveBroadcastImage(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (id: string) => api.delete<BroadcastDto>(`/broadcasts/${id}/image`),
    onSuccess: invalidate,
  });
}

export function useDeleteBroadcast(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/broadcasts/${id}`),
    onSuccess: invalidate,
  });
}

/** Goes to the author's own devices. Nothing else in the app can send only to yourself. */
export function useTestBroadcast() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ delivered: number; note: string }>(`/broadcasts/${id}/test`),
  });
}

/**
 * The irreversible one, and its own hook for that reason.
 *
 * Kept apart from `useTestBroadcast` rather than sharing one mutation with a flag, the
 * same way the server keeps the routes apart: a boolean would put "to me" and "to
 * everybody" one character from each other.
 */
export function useSendBroadcast(leagueId: string | null) {
  const invalidate = useBroadcastInvalidator(leagueId);
  return useMutation({
    mutationFn: (id: string) => api.post<BroadcastSendReportDto>(`/broadcasts/${id}/send`),
    onSuccess: invalidate,
  });
}

/**
 * The public landing page.
 *
 * No auth, no retry on a 404: a slug that does not resolve is a real answer — the
 * broadcast was deleted, or was never sent — not a transient failure worth hammering.
 */
export function useBroadcastLanding(slug: string | undefined) {
  return useQuery({
    queryKey: keys.broadcastLanding(slug ?? ''),
    queryFn: () => api.get<BroadcastLandingDto>(`/broadcasts/${slug}/landing`),
    enabled: Boolean(slug),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

/**
 * The whole roster in one request.
 *
 * Long `staleTime` because it changes a handful of times a season and only when somebody
 * presses Refresh — refetching it on every focus would be a hundred players of payload to
 * discover nothing moved.
 */
export function useRoster(year: number) {
  return useQuery({
    ...defaults,
    queryKey: keys.roster(year),
    queryFn: () => api.get<RosterDto>('/roster', { year }),
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Free — neither feed is metered — but slow, so the button carries a loading state. */
export function useSyncRoster(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RosterSyncReportDto>('/admin/sync/roster', { year }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.roster(year) });
    },
  });
}
